/**
 * Sub-account schemas — link / unlink sub-accounts to a master user.
 *
 * Legacy system used `fid` column on the users table; the new system uses
 * `master_id` with an explicit Prisma self-relation ("SubAccount").
 */
import { z } from 'zod';

// ---- Link / Unlink ---------------------------------------------------------

/** POST /api/admin/sub-accounts/link */
export const SubAccountLinkSchema = z.object({
  master_id: z.coerce.number().int().positive(),
  sub_user_id: z.coerce.number().int().positive(),
  /** sp1 | sp2 | sp3 — static exit multiplier for sub-account. */
  reinvest_coin_type: z.enum(['sp1', 'sp2', 'sp3']).optional(),
  /** E1 | E2 | E3 — principal package snapshot (defaults from master). */
  star_package: z.enum(['E1', 'E2', 'E3']).optional(),
  /** Binary leg side for tree placement — must match master 大区 (§4.2). */
  zone: z.enum(['L', 'R']),
});
export type SubAccountLinkRequest = z.infer<typeof SubAccountLinkSchema>;

/** POST /api/admin/sub-accounts/unlink */
export const SubAccountUnlinkSchema = z.object({
  sub_user_id: z.coerce.number().int().positive(),
});
export type SubAccountUnlinkRequest = z.infer<typeof SubAccountUnlinkSchema>;

// ---- List queries ----------------------------------------------------------

/** GET /api/admin/sub-accounts  (admin: list all relationships) */
export const SubAccountListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
  master_id: z.coerce.number().int().positive().optional(),
});
export type SubAccountListQuery = z.infer<typeof SubAccountListQuerySchema>;

/** GET /api/sub-accounts  (user: list my sub-accounts) */
export const SubAccountUserQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
});
export type SubAccountUserQuery = z.infer<typeof SubAccountUserQuerySchema>;

/** Master may pass `account_id` to view a linked sub-account (ys142857 子账户切换). */
export const ViewAsAccountQuerySchema = z.object({
  account_id: z.coerce.number().int().positive().optional(),
});
export type ViewAsAccountQuery = z.infer<typeof ViewAsAccountQuerySchema>;

// ---- User open sub-account -------------------------------------------------

/**
 * POST /api/sub-accounts/open — master opens a new sub-account in 大区.
 * Sub-accounts are switched under the master login (no separate login password).
 */
export const OpenSubAccountSchema = z.object({
  /** SP2 支付已取消 — only SP1 (95%+5%TP) and SP3 (90%+10%TP) remain. */
  reinvest_coin_type: z.enum(['sp1', 'sp3']),
  transaction_password: z.string().min(1).max(128),
  security_answer: z.string().min(1).max(256),
  position: z.enum(['L', 'R'], { required_error: '请选择左区或右区安置' }),
  /** Binary-tree node to start placement from (defaults to master). Must be master or downline. */
  placement_user_id: z.number().int().positive('安置节点无效').optional(),
  /** E1 | E2 | E3 — defaults from master level; E3 requires registration.e3_enabled. */
  star_package: z.enum(['E1', 'E2', 'E3']).optional(),
});

export type OpenSubAccountRequest = z.infer<typeof OpenSubAccountSchema>;

/** GET /api/sub-accounts/open/preview */
export const OpenSubAccountPreviewQuerySchema = z.object({
  placement_user_id: z.coerce.number().int().positive().optional(),
  star_package: z.enum(['E1', 'E2', 'E3']).optional(),
  /** Selected SP payment coin — determines the debit legs (SP1 95%+5%TP, SP3 90%+10%TP). */
  reinvest_coin_type: z.enum(['sp1', 'sp3']).optional(),
});
export type OpenSubAccountPreviewQuery = z.infer<typeof OpenSubAccountPreviewQuerySchema>;
